import { serviceSingletonGetter } from '@/services/serviceGetter';

export type ReminiaiFunnelService = {
  isRedirectedFromReminiAi: () => boolean;
  saveIsRedirectedFromReminiAi: () => void;
  clearIsRedirectedFromReminiAi: () => void;
  hasResolvedLanding: () => boolean;
  markLandingResolved: () => void;
};

export const REMINIAI_FUNNEL_QUERY_PARAM = 'reminiai_funnel' as const;
export const REMINIAI_FUNNEL_REDIRECT_SEGMENT = 'redirect_on_reminiai' as const;

const data = {} as Record<string, string>;

const memoryStorage = {
  setItem: (key: string, value: string) => {
    data[key] = value;
    return data[key];
  },
  removeItem: (key: string) => delete data[key],
  getItem: (key: string): string | null => data[key] ?? null,
  clear: () => Object.keys(data).forEach((key) => delete data[key]),
};

type WebStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const createReminiaiFunnelService = (): ReminiaiFunnelService => {
  const REDIRECTED_FROM_REMINIAI_KEY = 'isRedirectedFromReminiAi' as const;

  // Module state: persists across AppWrapper's key={router.route} remounts,
  // resets on a full page load — same lifetime as a landing resolution.
  let landingResolved = false;

  let storage: WebStorageLike | undefined;

  const getStorage = (): WebStorageLike => {
    if (storage) return storage;

    try {
      window.sessionStorage.setItem('checkAvailability', 'checkAvailability');
      window.sessionStorage.removeItem('checkAvailability');
      storage = window.sessionStorage;
    } catch (e) {
      // Privacy mode or blocked storage: fall back to in-memory so the flag
      // still lives for the current page lifetime.
      storage = memoryStorage;
    }

    return storage;
  };

  const isRedirectedFromReminiAi = (): boolean =>
    getStorage().getItem(REDIRECTED_FROM_REMINIAI_KEY) === 'true';

  const saveIsRedirectedFromReminiAi = () => {
    getStorage().setItem(REDIRECTED_FROM_REMINIAI_KEY, 'true');
  };

  const clearIsRedirectedFromReminiAi = () => {
    getStorage().removeItem(REDIRECTED_FROM_REMINIAI_KEY);
  };

  return {
    isRedirectedFromReminiAi,
    saveIsRedirectedFromReminiAi,
    clearIsRedirectedFromReminiAi,
    hasResolvedLanding: () => landingResolved,
    markLandingResolved: () => {
      landingResolved = true;
    },
  };
};

export default serviceSingletonGetter(
  Symbol('reminiaiFunnelService'),
  createReminiaiFunnelService
);
